/** @jsxImportSource @emotion/react */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  accessAPI,
  Titulo,
  Boton,
  SelectorArchivo
} from "../../../shared/index.js";
import { CardTotalesPendientes, Sidebar, tituloBase, usePermiso } from "../../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";
import CardFacturas from "./CardFacturas.js";
import CardPropuestasSabana from "./CardPropuestasSabana.js";
import useConBiller from "../../facturacion/useConBiller.js";

/**
 * Sección de facturas dentro del detalle del cliente. Dueña del estado de
 * la lista de facturas y del sidebar de "Nueva factura". CardFacturas sólo
 * muestra la tabla y maneja el detalle/descarga/marcar pagada de facturas
 * existentes.
 */
export default function ClienteFacturas() {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [razonsocial, setRazonsocial] = useState("");
  // Con lectura se ven las facturas; crear una pide full
  const { puedeEditar } = usePermiso("admin.facturacion");
  const [facturas, setFacturas] = useState([]);
  const [propuestasSabana, setPropuestasSabana] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estado del sidebar "Nueva factura"
  const [sidebarNueva, setSidebarNueva] = useState(false);
  const [loaderNueva, setLoaderNueva] = useState(false);
  const [actividadesDisponibles, setActividadesDisponibles] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [descuentosCliente, setDescuentosCliente] = useState([]);
  const [nuevaItems, setNuevaItems] = useState([]);
  const [nuevoItemTexto, setNuevoItemTexto] = useState("");
  const [nuevaMonedaId, setNuevaMonedaId] = useState(null);
  const [nuevaMasIva, setNuevaMasIva] = useState(true);
  const [nuevaAdenda, setNuevaAdenda] = useState("");

  // Facturar a otra empresa: arranca con la configurada en el cliente (si
  // hay) y el admin puede cambiarla por factura
  const [facturarAConfigId, setFacturarAConfigId] = useState(null);
  const [nuevaFacturarAId, setNuevaFacturarAId] = useState(null);
  const [clientesOpciones, setClientesOpciones] = useState([]);

  // Sin Biller el organismo emite el comprobante por fuera: el admin escribe el
  // número y, si ya lo tiene, adjunta el archivo.
  const conBiller = useConBiller();
  const [nuevaNumeroFactura, setNuevaNumeroFactura] = useState("");
  const [nuevaArchivo, setNuevaArchivo] = useState(null);

  const toast = useRef(null);

  function cargarFacturas() {
    accessAPI(
      "GET",
      `admin/facturacion/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setFacturas(respuesta);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setRazonsocial(respuesta.razonsocial);
        setFacturarAConfigId(respuesta.facturarAClienteId || null);
        document.title = `${respuesta.razonsocial} - Facturas`;
      },
      () => {}
    );
    cargarFacturas();
    accessAPI(
      "GET",
      `admin/propuesta/sabana/cliente/${clienteId}`,
      null,
      (respuesta) => setPropuestasSabana(respuesta || []),
      () => setPropuestasSabana([])
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  function abrirNuevaFactura() {
    setSidebarNueva(true);
    setNuevaItems([]);
    setNuevoItemTexto("");
    setNuevaMonedaId(null);
    setNuevaMasIva(true);
    setNuevaAdenda("");
    setNuevaFacturarAId(facturarAConfigId);

    accessAPI(
      "GET",
      "admin/cliente",
      null,
      (respuesta) => setClientesOpciones(respuesta || []),
      () => setClientesOpciones([])
    );
    accessAPI(
      "GET",
      `admin/facturacion/cliente/${clienteId}/actividades`,
      null,
      (respuesta) => setActividadesDisponibles(respuesta),
      () => {}
    );
    accessAPI(
      "GET",
      "admin/config/moneda?dropdown=true",
      null,
      (respuesta) => setMonedas(respuesta),
      () => {}
    );
    accessAPI(
      "GET",
      `admin/cliente/${clienteId}/descuentos`,
      null,
      (respuesta) => setDescuentosCliente(respuesta || []),
      () => setDescuentosCliente([])
    );
  }

  // Busca el descuento aplicable para un certificadoTipoId según los descuentos
  // actualmente cargados. Se resuelve en cada render para evitar problemas de
  // timing cuando los descuentos llegan después de agregar el ítem.
  function descuentoParaCertificadoTipo(certificadoTipoId) {
    if (!certificadoTipoId) return null;
    return (
      descuentosCliente.find(
        (d) => d.certificadoTipo?.id === certificadoTipoId
      ) || null
    );
  }

  function agregarItem(actividadValue) {
    const actividad = actividadesDisponibles.find(
      (a) => a.value === actividadValue
    );
    if (!actividad) return;
    if (nuevaItems.find((i) => i.actividadid === actividad.value)) return;
    setNuevaItems((prev) => [
      ...prev,
      {
        _key: `act-${actividad.value}`,
        actividadid: actividad.value,
        descripcion: actividad.etiqueta,
        procesoid: actividad.procesoid,
        certificadoTipoId: actividad.certificadoTipoId || null,
        precio: 0,
        // Por defecto aplicamos el descuento si existe; el usuario puede
        // desmarcarlo. Se guarda explícitamente para respetar la decisión.
        aplicarDescuento: true
      }
    ]);
  }

  // Agrega un ítem escrito a mano (sin actividad asociada). Va al mismo array
  // de items y se persiste igual que el resto (con actividadid/procesoid nulos).
  function agregarItemManual() {
    if (!nuevoItemTexto.trim()) return;
    setNuevaItems((prev) => [
      ...prev,
      {
        _key: `manual-${Date.now()}`,
        descripcion: nuevoItemTexto.trim(),
        precio: 0
      }
    ]);
    setNuevoItemTexto("");
  }

  function toggleAplicarDescuento(key, aplicar) {
    setNuevaItems((prev) =>
      prev.map((item) =>
        item._key === key ? { ...item, aplicarDescuento: aplicar } : item
      )
    );
  }

  function actualizarPrecioItem(key, precio) {
    setNuevaItems((prev) =>
      prev.map((item) => (item._key === key ? { ...item, precio } : item))
    );
  }

  function eliminarItem(key) {
    setNuevaItems((prev) => prev.filter((item) => item._key !== key));
  }

  function emitirFacturaDirecta() {
    if (nuevaItems.length === 0 || !nuevaMonedaId) return;
    const sinPrecio = nuevaItems.some((i) => !i.precio || i.precio <= 0);
    if (sinPrecio) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Todos los items deben tener un precio mayor a 0",
        sticky: true
      });
      return;
    }

    setLoaderNueva(true);
    const itemsPayload = nuevaItems.map((i) => {
      const descuento = descuentoParaCertificadoTipo(i.certificadoTipoId);
      return {
        actividadid: i.actividadid,
        descripcion: i.descripcion,
        procesoid: i.procesoid,
        precio: i.precio,
        descuentoId: i.aplicarDescuento && descuento ? descuento.id : null
      };
    });
    accessAPI(
      "POST",
      "admin/facturacion/emitirdirecta",
      {
        clienteId: Number(clienteId),
        facturarAId: nuevaFacturarAId || null,
        items: itemsPayload,
        monedaId: nuevaMonedaId,
        masIva: nuevaMasIva,
        adenda: nuevaAdenda.trim() || null,
        ...(conBiller ? {} : { numeroFactura: nuevaNumeroFactura.trim() })
      },
      (respuesta) => {
        // El archivo va en un request aparte: el alta manda los ítems como JSON
        // anidado y un FormData los aplastaría a texto.
        const terminar = () => {
          setLoaderNueva(false);
          setSidebarNueva(false);
          setNuevaNumeroFactura("");
          setNuevaArchivo(null);
          toast.current?.show({
            severity: "success",
            summary: "Éxito",
            detail: respuesta.msg || "Factura emitida exitosamente"
          });
          cargarFacturas();
        };

        if (!nuevaArchivo || !respuesta.factura?.id) return terminar();
        accessAPI(
          "POST",
          `admin/facturacion/${respuesta.factura.id}/comprobante/factura`,
          null,
          terminar,
          (fallo) => {
            terminar();
            toast.current?.show({
              severity: "warn",
              summary: "La factura se emitió, pero el archivo no se pudo subir",
              detail:
                (fallo?.[0]?.msg || "") +
                " Se puede subir después desde el detalle de la factura.",
              sticky: true
            });
          },
          nuevaArchivo
        );
      },
      (respuesta) => {
        setLoaderNueva(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al emitir factura",
          sticky: true
        });
      }
    );
  }

  if (loading || !razonsocial) {
    return (
      <>
        <Toast ref={toast} position="top-center" />
        <div
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "2em",
            marginBottom: "1em"
          }}
        >
          <Skeleton width="20rem" height="2rem" />
          <Skeleton width="10rem" height="2.5rem" />
        </div>
        <Card>
          <Skeleton
            width="100%"
            height="2.5rem"
            css={{ marginBottom: "0.5em" }}
          />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              width="100%"
              height="3rem"
              css={{ marginBottom: "0.3em" }}
            />
          ))}
        </Card>
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        botones={
          <>
            <Boton
              transparente
              callback={() => navigate(`/propuestasabana/nueva/${clienteId}`)}
            >
              Nueva propuesta sábana
            </Boton>
            {puedeEditar && (
              <Boton callback={abrirNuevaFactura}>Nueva factura</Boton>
            )}
          </>
        }
      >
        {razonsocial ? `${razonsocial} - Facturas` : "Facturas"}
      </Titulo>

      <CardPropuestasSabana propuestas={propuestasSabana} />

      {!loading && <CardTotalesPendientes facturas={facturas} />}

      <CardFacturas
        facturas={facturas}
        loading={loading}
        onRecargar={cargarFacturas}
      />

      <Sidebar
        visible={sidebarNueva}
        onHide={() => setSidebarNueva(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nueva factura</Titulo>

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Facturar a
          </label>
          <Dropdown
            value={nuevaFacturarAId}
            onChange={(e) => setNuevaFacturarAId(e.value ?? null)}
            options={clientesOpciones}
            optionLabel="etiqueta"
            optionValue="value"
            placeholder="Este cliente"
            showClear
            filter
            css={{ width: "100%" }}
          />
        </div>

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Moneda
          </label>
          <Dropdown
            value={nuevaMonedaId}
            onChange={(e) => setNuevaMonedaId(e.value)}
            options={monedas}
            optionLabel="etiqueta"
            optionValue="value"
            placeholder="Seleccionar moneda"
            css={{ width: "100%" }}
          />
        </div>

        <div
          css={{
            display: "flex",
            alignItems: "center",
            gap: "0.75em",
            marginBottom: "1.5em"
          }}
        >
          <InputSwitch
            checked={nuevaMasIva}
            onChange={(e) => setNuevaMasIva(e.value)}
          />
          <span>Más IVA</span>
        </div>

        <Divider />

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Agregar actividad
          </label>
          <Dropdown
            value={null}
            onChange={(e) => agregarItem(e.value)}
            options={actividadesDisponibles.filter(
              (a) => !nuevaItems.find((i) => i.actividadid === a.value)
            )}
            optionLabel="etiqueta"
            optionValue="value"
            placeholder="Seleccionar actividad"
            css={{ width: "100%" }}
            filter
          />
        </div>

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Agregar ítem manual
          </label>
          <div css={{ display: "flex", gap: "0.5em" }}>
            <InputText
              value={nuevoItemTexto}
              onChange={(e) => setNuevoItemTexto(e.target.value)}
              placeholder="Descripción del ítem"
              css={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") agregarItemManual();
              }}
            />
            <Boton
              callback={agregarItemManual}
              disabled={!nuevoItemTexto.trim()}
            >
              <i className="pi pi-plus" />
            </Boton>
          </div>
        </div>

        {nuevaItems.length > 0 && (
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75em",
              marginBottom: "1.5em"
            }}
          >
            {nuevaItems.map((item) => {
              const descuento = descuentoParaCertificadoTipo(
                item.certificadoTipoId
              );
              return (
                <div
                  key={item._key}
                  css={{
                    padding: "0.75em",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "1px solid #e0e0e0"
                  }}
                >
                  <div
                    css={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "0.5em"
                    }}
                  >
                    <span css={{ fontSize: "0.9em" }}>{item.descripcion}</span>
                    <Boton
                      texto
                      rojo
                      chico
                      callback={() => eliminarItem(item._key)}
                    >
                      <i className="pi pi-times" />
                    </Boton>
                  </div>
                  <InputNumber
                    value={item.precio}
                    onValueChange={(e) =>
                      actualizarPrecioItem(item._key, e.value || 0)
                    }
                    mode="decimal"
                    minFractionDigits={0}
                    placeholder="Precio"
                    inputStyle={{ width: "100%" }}
                    css={{ width: "100%" }}
                  />
                  {descuento && (
                    <div
                      css={{
                        marginTop: "0.5em",
                        padding: "0.5em 0.75em",
                        backgroundColor: "#fff8e1",
                        border: "1px solid var(--amarillo)",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5em"
                      }}
                    >
                      <Checkbox
                        inputId={`descuento-${item._key}`}
                        checked={item.aplicarDescuento}
                        onChange={(e) =>
                          toggleAplicarDescuento(item._key, e.checked)
                        }
                      />
                      <label
                        htmlFor={`descuento-${item._key}`}
                        css={{
                          fontSize: "0.85em",
                          cursor: "pointer",
                          lineHeight: 1.4
                        }}
                      >
                        <strong>
                          Descuento {descuento.porcentajeDescuento}%
                        </strong>
                        {descuento.concepto ? ` - ${descuento.concepto}` : ""}
                        {item.aplicarDescuento && item.precio > 0 && (
                          <div css={{ color: "var(--grisTextos)" }}>
                            Final:{" "}
                            {(
                              item.precio *
                              (1 - descuento.porcentajeDescuento / 100)
                            ).toFixed(2)}
                          </div>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: "bold",
                padding: "0.5em 0"
              }}
            >
              <span>Total</span>
              <span>
                {monedas
                  .find((m) => m.value === nuevaMonedaId)
                  ?.etiqueta?.split(" ")[0] || ""}{" "}
                {nuevaItems
                  .reduce((sum, i) => {
                    const precio = i.precio || 0;
                    const d = i.aplicarDescuento
                      ? descuentoParaCertificadoTipo(i.certificadoTipoId)
                      : null;
                    if (d)
                      return sum + precio * (1 - d.porcentajeDescuento / 100);
                    return sum + precio;
                  }, 0)
                  .toFixed(2)}
                {nuevaMasIva && " + IVA"}
              </span>
            </div>
          </div>
        )}

        {conBiller === false && (
          <div css={{ marginBottom: "1.5em" }}>
            <label
              css={{
                fontWeight: "bold",
                display: "block",
                marginBottom: "0.5em"
              }}
            >
              Número de factura
            </label>
            <InputText
              value={nuevaNumeroFactura}
              onChange={(e) => setNuevaNumeroFactura(e.target.value)}
              css={{ width: "100%" }}
            />
            <small css={{ color: "var(--grisTextos)" }}>
              El del comprobante que ya emitió el organismo. Es el que ve el
              cliente.
            </small>

            <label
              css={{
                fontWeight: "bold",
                display: "block",
                margin: "1em 0 0.5em"
              }}
            >
              Archivo de la factura
            </label>
            <SelectorArchivo
              archivo={nuevaArchivo}
              onSeleccion={setNuevaArchivo}
              etiqueta="Elegir factura"
            />
            <small css={{ display: "block", color: "var(--grisTextos)" }}>
              Opcional. Si todavía no lo tenés, se puede subir después desde el
              detalle de la factura.
            </small>
          </div>
        )}

        <div css={{ marginBottom: "1em" }}>
          <label
            css={{
              fontWeight: "bold",
              display: "block",
              marginBottom: "0.5em"
            }}
          >
            Adenda
          </label>
          <InputTextarea
            value={nuevaAdenda}
            onChange={(e) => setNuevaAdenda(e.target.value)}
            placeholder="Texto adicional para la adenda de la factura"
            rows={3}
            autoResize
            css={{ width: "100%" }}
          />
        </div>

        <Boton
          callback={emitirFacturaDirecta}
          loader={loaderNueva}
          disabled={
            nuevaItems.length === 0 ||
            !nuevaMonedaId ||
            (conBiller === false && !nuevaNumeroFactura.trim())
          }
          ancho
        >
          Emitir factura
        </Boton>
      </Sidebar>
    </>
  );
}
